import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  recommendations as api,
  type Recommendation,
  type RecommendationsResponse,
} from "@/lib/api";
import Spinner from "@/components/Spinner";
import {
  Sparkles, RefreshCw, AlertTriangle, ChevronRight,
  Shield, Users, TrendingUp, Zap, Settings, DollarSign,
  CheckCircle, Mail, BarChart3, Target,
} from "lucide-react";

const CATEGORY_META: Record<string, { label: string; icon: any; color: string }> = {
  setup:          { label: "Setup",          icon: Settings,   color: "bg-purple-50 text-purple-600 border-purple-200" },
  deliverability: { label: "Deliverability", icon: Shield,     color: "bg-red-50 text-red-600 border-red-200" },
  engagement:     { label: "Engagement",     icon: Users,      color: "bg-amber-50 text-amber-600 border-amber-200" },
  growth:         { label: "Growth",         icon: TrendingUp, color: "bg-blue-50 text-blue-600 border-blue-200" },
  optimization:   { label: "Optimization",   icon: Zap,        color: "bg-teal-light text-teal border-teal/20" },
  revenue:        { label: "Revenue",        icon: DollarSign, color: "bg-emerald-50 text-emerald-600 border-emerald-200" },
};

const PRIORITY_STYLE = {
  high:   { bg: "border-l-red-500",    badge: "bg-red-100 text-red-700" },
  medium: { bg: "border-l-amber-400",  badge: "bg-amber-100 text-amber-700" },
  low:    { bg: "border-l-slate/30",   badge: "bg-slate/10 text-slate" },
};

export default function RecommendationsPage() {
  const [data, setData] = useState<RecommendationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  async function load() {
    try {
      const res = await api.get();
      setData(res);
    } catch (e) {
      console.error("Recommendations error:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => { load(); }, []);

  function handleRefresh() {
    setRefreshing(true);
    load();
  }

  function handleDismiss(id: string) {
    setDismissed(prev => new Set(prev).add(id));
  }

  if (loading) {
    return <div className="max-w-4xl mx-auto px-4 py-20 flex justify-center"><Spinner /></div>;
  }

  const visibleRecs = (data?.recommendations || []).filter(r => !dismissed.has(r.id));
  const highCount = visibleRecs.filter(r => r.priority === "high").length;
  const medCount = visibleRecs.filter(r => r.priority === "medium").length;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-ink flex items-center gap-2">
            <Sparkles size={22} className="text-coral" /> Smart Recommendations
          </h1>
          <p className="text-slate text-sm mt-0.5">
            Personalized suggestions based on your account data.
          </p>
        </div>
        <button onClick={handleRefresh} disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-sand text-sm font-medium text-slate hover:bg-paper transition-colors disabled:opacity-50">
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Summary bar */}
      {data && (
        <div className="bg-white rounded-xl border border-sand p-4 mb-6 flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-coral" />
            <span className="text-sm font-semibold text-ink">{visibleRecs.length} recommendation{visibleRecs.length !== 1 ? "s" : ""}</span>
          </div>
          {highCount > 0 && (
            <span className="flex items-center gap-1 text-xs font-semibold text-red-600">
              <AlertTriangle size={12} /> {highCount} urgent
            </span>
          )}
          {medCount > 0 && (
            <span className="flex items-center gap-1 text-xs font-semibold text-amber-600">
              <AlertTriangle size={12} /> {medCount} important
            </span>
          )}

          {/* Account snapshot */}
          <div className="ml-auto flex items-center gap-4 text-[11px] text-slate">
            {data.snapshot.subscribers > 0 && (
              <span className="flex items-center gap-1"><Users size={11} /> {data.snapshot.subscribers.toLocaleString()} subs</span>
            )}
            {data.snapshot.openRate !== null && (
              <span className="flex items-center gap-1"><Mail size={11} /> {data.snapshot.openRate}% opens</span>
            )}
            {data.snapshot.automations > 0 && (
              <span className="flex items-center gap-1"><Zap size={11} /> {data.snapshot.automations} active</span>
            )}
          </div>
        </div>
      )}

      {/* No recommendations */}
      {visibleRecs.length === 0 && (
        <div className="bg-white rounded-xl border border-sand py-16 text-center">
          <CheckCircle size={40} className="mx-auto text-emerald-500 mb-3" />
          <h3 className="font-display font-bold text-ink text-lg">All Good!</h3>
          <p className="text-sm text-slate mt-1 max-w-sm mx-auto">
            No recommendations right now. Your email program is in good shape. Keep it up!
          </p>
        </div>
      )}

      {/* Recommendation cards */}
      {visibleRecs.length > 0 && (
        <div className="space-y-3">
          {visibleRecs.map((rec) => {
            const catMeta = CATEGORY_META[rec.category] || CATEGORY_META.optimization;
            const CatIcon = catMeta.icon;
            const prioStyle = PRIORITY_STYLE[rec.priority];

            return (
              <div key={rec.id}
                className={`bg-white rounded-xl border border-sand border-l-4 ${prioStyle.bg} p-5 transition-all hover:shadow-sm`}>
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border ${catMeta.color}`}>
                    <CatIcon size={18} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-sm font-bold text-ink">{rec.title}</h3>
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${prioStyle.badge}`}>
                        {rec.priority}
                      </span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${catMeta.color}`}>
                        {catMeta.label}
                      </span>
                    </div>

                    <p className="text-xs text-slate leading-relaxed mb-3">{rec.description}</p>

                    {/* Metric + action */}
                    <div className="flex items-center gap-3 flex-wrap">
                      {rec.metric && (
                        <div className="bg-paper rounded-lg px-2.5 py-1 flex items-center gap-1.5">
                          <span className="font-display font-bold text-ink text-sm">{rec.metric}</span>
                          <span className="text-[10px] text-slate">{rec.metricLabel}</span>
                        </div>
                      )}

                      <Link to={rec.actionLink}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-coral hover:underline">
                        {rec.actionLabel} <ChevronRight size={12} />
                      </Link>

                      <button onClick={() => handleDismiss(rec.id)}
                        className="text-[10px] text-slate/50 hover:text-slate ml-auto">
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dismissed count */}
      {dismissed.size > 0 && (
        <p className="text-xs text-slate/50 text-center mt-4">
          {dismissed.size} recommendation{dismissed.size !== 1 ? "s" : ""} dismissed.{" "}
          <button onClick={() => setDismissed(new Set())} className="text-coral hover:underline">
            Show all
          </button>
        </p>
      )}
    </div>
  );
}
